import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

interface SpacePingsGoogleAuthProps {
  onSignIn: () => Promise<void>;
  isReturningUser: boolean;
  isLoading: boolean;
  disabled?: boolean;
}

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
  50% { box-shadow: 0 0 30px rgba(102, 126, 234, 0.5); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

// Styled Components
const AuthContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 24px;
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.95) 0%,
    rgba(30, 41, 59, 0.95) 50%,
    rgba(15, 23, 42, 0.95) 100%
  );
  border-radius: 20px;
  border: 2px solid transparent;
  background-clip: padding-box;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, #667eea, #764ba2, #f093fb);
    border-radius: 20px;
    padding: 2px;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: subtract;
    animation: ${glow} 3s ease-in-out infinite;
  }
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

const SpacePingsLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  animation: ${float} 3s ease-in-out infinite;
`;

const LogoIcon = styled.span`
  font-size: 2.5rem;
  margin-right: 12px;
  filter: drop-shadow(0 0 10px rgba(102, 126, 234, 0.5));
`;

const LogoText = styled.span`
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const AuthTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const AuthSubtitle = styled.p`
  color: #cbd5e1;
  font-size: 1rem;
  margin: 0 0 16px 0;
  opacity: 0.9;
  line-height: 1.4;
`;

const BrandingNotice = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const BrandingIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 8px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const BrandingText = styled.p`
  color: #93c5fd;
  font-size: 0.9rem;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const BrandingSubtext = styled.p`
  color: #cbd5e1;
  font-size: 0.8rem;
  margin: 0;
  opacity: 0.8;
  line-height: 1.3;
`;

const GoogleAuthButton = styled.button<{ $isHovered: boolean; $isLoading: boolean }>`
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.$isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.$isLoading ? 0.7 : 1};
  transform: ${props => props.$isHovered && !props.$isLoading ? 'translateY(-2px)' : 'translateY(0)'};
  box-shadow: ${props => props.$isHovered && !props.$isLoading 
    ? '0 10px 30px rgba(66, 133, 244, 0.4)' 
    : '0 5px 15px rgba(66, 133, 244, 0.3)'
  };

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const GoogleIcon = styled.div<{ $isLoading: boolean }>`
  width: 24px;
  height: 24px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
    opacity: ${props => props.$isLoading ? 0.5 : 1};
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SparkleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const Sparkle = styled.div<{ $delay: number; $size: number; $left: number; $top: number }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  background: #ffffff;
  border-radius: 50%;
  left: ${props => props.$left}%;
  top: ${props => props.$top}%;
  animation: ${sparkle} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
`;

const SpacePingsGoogleAuth: React.FC<SpacePingsGoogleAuthProps> = ({
  onSignIn,
  isReturningUser,
  isLoading,
  disabled = false
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = async () => {
    if (disabled || isLoading) return;
    await onSignIn();
  };

  // Generate sparkles
  const sparkles = Array.from({ length: 8 }, (_, i) => ({
    delay: i * 0.5,
    size: Math.random() * 3 + 2,
    left: Math.random() * 100,
    top: Math.random() * 100,
  }));

  return (
    <AuthContainer>
      <SparkleContainer>
        {sparkles.map((sparkle, index) => (
          <Sparkle
            key={index}
            $delay={sparkle.delay}
            $size={sparkle.size}
            $left={sparkle.left}
            $top={sparkle.top}
          />
        ))}
      </SparkleContainer>

      <AuthHeader>
        <SpacePingsLogo>
          <LogoIcon>🚀</LogoIcon>
          <LogoText>SpacePings</LogoText>
        </SpacePingsLogo>
        
        <AuthTitle>
          {isReturningUser ? 'Welcome Back, Space Explorer' : 'Join the SpacePings Mission'}
        </AuthTitle>
        
        <AuthSubtitle>
          {isReturningUser 
            ? 'Continue your journey through the cosmos'
            : 'Real-time satellite tracking • Launch alerts • Space weather intelligence'
          }
        </AuthSubtitle>
      </AuthHeader>

      <BrandingNotice>
        <BrandingIcon>🌌</BrandingIcon>
        <BrandingText>Signing into SpacePings</BrandingText>
        <BrandingSubtext>
          You'll be redirected to Google to securely sign in to your SpacePings account. 
          Look for "SpacePings" in the authorization popup.
        </BrandingSubtext>
      </BrandingNotice>

      <GoogleAuthButton
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={disabled || isLoading}
        $isHovered={isHovered}
        $isLoading={isLoading}
      >
        <ButtonContent>
          <GoogleIcon $isLoading={isLoading}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#ffffff"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#ffffff"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#ffffff"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#ffffff"
                />
              </svg>
            )}
          </GoogleIcon>
          {isLoading ? 'Launching Authentication...' : 'Continue with Google'}
        </ButtonContent>
      </GoogleAuthButton>
    </AuthContainer>
  );
};

export default SpacePingsGoogleAuth;